import { defineStore } from "pinia";
import router from "@/router";
import { fetchWrapper } from "@/helpers/fetch-wrapper";
import { useCurrentStore } from "@/stores/current";
import { useErrorStore } from "@/stores/error.store";
import { useUserStore } from "@/stores/user.store"; // ⬅️ do pobrania updated_at

const SESSION_TOUCH_INTERVAL_MS = 20000;

// Store Pinia odpowiadający za uwierzytelnianie użytkownika oraz sesję
export const useAuthStore = defineStore({
  id: "auth",
  state: () => ({
    token: sessionStorage.getItem("cvToken"), // Token JWT
    returnUrl: "", // Redirect po logowaniu
    pingLoop: false, // ID interwału pingowania
    current: null, // opcjonalnie bieżący user
    mustChangePassword: sessionStorage.getItem("mustChangePwd") === "1", // <---
    useErrorStore: useErrorStore(),
    skipPwdFreshCheckOnce: false,   // przepuść najbliższą nawigację bez sprawdzania ważności
    localPasswordChangedAt: null,   // lokalny „znacznik czasu” zmiany hasła
    suspendHeartbeatsUntilUserAction: false,
    sessionTouchEnabled: false,
    lastSessionTouchAt: 0,
  }),

  actions: {
    // helper
    _setMustChangePassword(val) {
      this.mustChangePassword = !!val;
      if (val) sessionStorage.setItem("mustChangePwd", "1");
      else sessionStorage.removeItem("mustChangePwd");
    },

    // 90 dni
    _isPasswordExpired(updatedAt) {
      if (!updatedAt) return true;
      const ts = new Date(updatedAt).getTime();
      return Date.now() - ts > 90 * 24 * 60 * 60 * 1000;
    },

    // Logowanie użytkownika - pobiera token, zapisuje go do sessionStorage
      async login(username, password) {
          try {
              const authRes = await fetchWrapper.post(
                  `${process.env.VUE_APP_API_URL}/login`,
                  { username, password }
              );

              this.token = authRes.token;
              sessionStorage.setItem("cvToken", authRes.token);
              this.useErrorStore.clearErrorMessage();

              // >>> KLUCZ: wyczyść poprzednie dane usera
              const userStore = useUserStore();
              userStore.reset();

              const passwordExpired = await this.ensurePasswordFreshness();

              if (passwordExpired) {
                  this.useErrorStore.showErrorMessage(
                      "Twoje hasło straciło ważność. Ustaw nowe hasło, aby kontynuować."
                  );
                  router.push("/password-expired");
              } else {
                  this.startInactivityWatch();
                  this.ping(false);

                  router.push(
                      typeof this.returnUrl === "string" && this.returnUrl.length > 0
                          ? this.returnUrl
                          : "/"
                  );
              }
          } catch (error) {
              this.useErrorStore.showErrorMessage(error);
          }
      },

      // 🔍 Sprawdź ważność hasła (3 miesiące od updated_at)
    // async ensurePasswordFreshness() {
    //   try {
    //     const userStore = useUserStore();
    //     if (typeof userStore.self === "function") {
    //       await userStore.self();
    //     }

    //     const user = userStore.currentUser || {};

    //     console.log('user:', JSON.stringify(user));
    //     // Elastycznie: bierzemy cokolwiek serwer zwraca
    //     const updatedAtRaw = user.passwordUpdatedAt || user.updatedAt;

    //     if (!updatedAtRaw) {
    //       // jeśli nie mamy daty – uznajemy, że nie wygasło
    //       return false;
    //     }

    //     const updatedAt = new Date(updatedAtRaw);
    //     if (isNaN(updatedAt.getTime())) {
    //       // jeśli data niepoprawna – uznajemy, że nie wygasło
    //       return false;
    //     }

    //     // 3 miesiące od updated_at
    //     const expiry = new Date(updatedAt.getTime());
    //     expiry.setMonth(expiry.getMonth() + 3);

    //     return new Date() > expiry; // true => hasło wygasło
    //   } catch {
    //     // w razie błędu nie blokuj użytkownika – traktuj jako ważne
    //     return false;
    //   }
    // },

    async ensurePasswordFreshness() {
      try {
        // 0) Jednorazowa amnestia zaraz po zmianie hasła
        if (this.skipPwdFreshCheckOnce) {
          this.skipPwdFreshCheckOnce = false;
          return false; // NIE traktuj jako wygasłe
        }

        const userStore = useUserStore();
        if (typeof userStore.self === "function") {
          await userStore.self();
        }
        const u = userStore.currentUser || {};

        // helper do parsowania daty
        const asDate = (v) => {
          if (!v) return null;
          const d = new Date(v);
          return isNaN(d) ? null : d;
        };

        // 1) Jeśli mamy lokalny znacznik zmiany – użyj go (eliminuje lag backendu)
        let changedAt =
          (this.localPasswordChangedAt && new Date(this.localPasswordChangedAt)) ||
          asDate(u.passwordUpdatedAt) ||
          asDate(u.password_updated_at) ||
          asDate(u.updatedAt) ||
          asDate(u.updated_at);

        // 2) Fallback – jeśli mamy passwordExpiration, to też możemy użyć
        const explicitExpiration =
          asDate(u.passwordExpiration) || asDate(u.password_expiration);

        if (changedAt) {
          const expiry = new Date(changedAt.getTime());
          expiry.setMonth(expiry.getMonth() + 3); // 3 miesiące ważności
          return new Date() > expiry;
        }

        if (explicitExpiration) {
          return new Date() > explicitExpiration;
        }

        // Brak jakichkolwiek dat => nie blokuj
        return false;
      } catch {
        // W razie błędu nie blokuj
        return false;
      }
    },

    // Włącza/wyłącza odświeżanie sesji backendu przez aktywność użytkownika
    async ping(disable) {
      if (disable) {
        this.sessionTouchEnabled = false;
        this.pingLoop = false;
      } else {
        if (!this.token) {
          this.sessionTouchEnabled = false;
          this.pingLoop = false;
          return;
        }
        this.sessionTouchEnabled = true;
        this.pingLoop = true;
        await this.touchSession("ping-enable");
      }
    },

    // 🔒 Start/Stop auto-logout po 5 min braku aktywności
    // startInactivityWatch() {
    //   this.stopInactivityWatch(); // safety
    //   this._activityEvents = [
    //     "mousemove",
    //     "keydown",
    //     "click",
    //     "touchstart",
    //     "scroll",
    //   ];
    //   this._boundActivityHandler = () => this.resetInactivityTimer();
    //
    //   this._activityEvents.forEach((evt) => {
    //     window.addEventListener(evt, this._boundActivityHandler, {
    //       passive: true,
    //     });
    //   });
    //
    //   this.resetInactivityTimer();
    // },
    //
    // stopInactivityWatch() {
    //   if (this._activityEvents && this._boundActivityHandler) {
    //     this._activityEvents.forEach((evt) => {
    //       window.removeEventListener(evt, this._boundActivityHandler);
    //     });
    //   }
    //   this._activityEvents = null;
    //   this._boundActivityHandler = null;
    //
    //   if (this.inactivityTimer) {
    //     clearTimeout(this.inactivityTimer);
    //     this.inactivityTimer = null;
    //   }
    // },

    // resetInactivityTimer() {
    //   if (this.inactivityTimer) clearTimeout(this.inactivityTimer);
    //   // 5 minut = 300000 ms
    //   this.inactivityTimer = setTimeout(() => {
    //     this.useErrorStore.showErrorMessage(
    //       "Zostałeś wylogowany z powodu braku aktywności (5 min)."
    //     );
    //     this.logout();
    //   }, 5 * 60 * 1000);
    // },

    // onPasswordChanged: async function() {
    //   try {
    //     const userStore = useUserStore();
    //     await userStore.self?.(); // świeży updated_at/passwordUpdatedAt
    //   } catch {
    //     // ignoruj
    //   }
    //   this._setMustChangePassword(false);
    //   this.startInactivityWatch();
    //   this.ping(false);
    // },
      startInactivityWatch() {
          this.stopInactivityWatch(); // safety

          // Prawdziwa aktywność użytkownika = odświeżenie sesji na backendzie
          this._activityEvents = ["mousemove", "keydown", "click", "touchstart", "scroll"];
          this._onUserActivity = () => this.onUserActivity();

          this._activityEvents.forEach((evt) => {
              window.addEventListener(evt, this._onUserActivity, { passive: true });
          });

          // Gdy karta wraca na wierzch, odśwież sesję tylko raz.
          this._onVisibilityChange = () => {
              if (!document.hidden) this.heartbeat("visibilitychange");
          };
          document.addEventListener("visibilitychange", this._onVisibilityChange);

          // Sygnał przy zamykaniu/odświeżeniu karty.
          this._onBeforeUnload = () => this.sendLeaveRequest();
          window.addEventListener("beforeunload", this._onBeforeUnload);
      },

      stopInactivityWatch() {
          if (this._activityEvents && this._onUserActivity) {
              this._activityEvents.forEach((evt) => {
                  window.removeEventListener(evt, this._onUserActivity);
              });
          }
          if (this._onVisibilityChange) {
              document.removeEventListener("visibilitychange", this._onVisibilityChange);
          }
          if (this._onBeforeUnload) {
              window.removeEventListener("beforeunload", this._onBeforeUnload);
          }
          this._activityEvents = null;
          this._onUserActivity = null;
          this._onVisibilityChange = null;
          this._onBeforeUnload = null;

      },

      onUserActivity() {
          this.heartbeat("user-activity");
      },

      /**
       * “Bicie serca” z aplikacji (np. po aktywności użytkownika).
       * Frontend nie trzyma już sesji stałym interwałem.
       */
      heartbeat(source = "app") {
          if (!this.sessionTouchEnabled || !this.token) return;
          if (document.hidden) return;

          this.touchSession(source);
      },

      async touchSession(source = "activity") {
          if (!this.token) return;

          const now = Date.now();
          if (now - this.lastSessionTouchAt < SESSION_TOUCH_INTERVAL_MS) return;

          this.lastSessionTouchAt = now;

          try {
              await fetchWrapper.get(`${process.env.VUE_APP_API_URL}/ping`);
          } catch (error) {
              // 401/403 obsługuje globalnie fetchWrapper
              console.debug("Session touch failed", source, error);
          }
      },
    sendLeaveRequest() {
      if (!this.token || typeof fetch === "undefined") {
        return;
      }

      fetch(`${process.env.VUE_APP_API_URL}/login`, {
        method: "DELETE",
        keepalive: true,
        headers: {
          Authorization: `Bearer ${this.token}`,
        },
      }).catch(() => {});
    },
    onPasswordChanged: async function() {
      // 1) zdejmij wymuszanie zmiany hasła
      this._setMustChangePassword(false);

      // 2) ustaw lokalny „czas zmiany” i jednorazowe pominięcie sprawdzania
      this.localPasswordChangedAt = new Date();
      this.skipPwdFreshCheckOnce = true;

      // 3) spróbuj dociągnąć świeżego usera (jak backend już zaktualizował daty)
      try {
        const userStore = useUserStore();
        await userStore.self?.();
      } catch {
        console.log('onPasswordChanged: nie udało się pobrać danych użytkownika');
      }

      // 4) wznów zegary sesji
      this.startInactivityWatch();
      this.ping(false);
    },

    // Wylogowuje użytkownika (usuwa token, czyści wszystko, przekierowuje na /login)
    logout() {
      const current = useCurrentStore();
      current.stopLogout(); // czyści interwały i dane bieżące

      // zatrzymaj ping i nasłuch bezczynności
      this.ping(true);
      this.stopInactivityWatch();

      fetchWrapper.delete(`${process.env.VUE_APP_API_URL}/login`);
      this.token = null;
      this.pingLoop = false;
      this.sessionTouchEnabled = false;
      this.lastSessionTouchAt = 0;
      sessionStorage.removeItem("cvToken");
        const userStore = useUserStore();
        userStore.reset();
        router.push("/login");
    },

    // Resetowanie hasła (wysyłka maila z linkiem)
    async resetPassword(email) {
      try {
        await fetchWrapper.put(
          `${process.env.VUE_APP_API_URL}/reset-password`,
          { email }
        );
        router.push({
          path: `/reset-password/reset/${encodeURIComponent(email)}`,
        });
      } catch (error) {
        this.useErrorStore.showErrorMessage(error);
      }
    },

    // Ustawienie nowego hasła po wejściu z linka z maila (token z URL)
    async setPassword(email, password, token) {
      try {
        const response = await fetchWrapper.put(
          `${process.env.VUE_APP_API_URL}/set-password`,
          { email, password, token }
        );
        return response;
      } catch (error) {
        console.log("Error in setPassword:", error);
        return error;
      }
    },

    // Czyści komunikat o błędzie (np. po wyświetleniu na UI)
    clearErrorMessage() {
      this.useErrorStore.clearErrorMessage();
    },
  },
});
